import { AddRingFill } from '@repo/go-icons';
import { Tooltip } from '@repo/go-ui';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { TGroup } from 'src/entities/tab/TabEntity';
import { useService } from 'src/packages/di';
import { useOutsideClick } from 'src/packages/shared/hooks/use-outside-click';

import styles from './GroupSelect.module.scss';
import type { GroupSelectStore } from './GroupSelect.store';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupSelectStore: GroupSelectStore;
  onChangeGroup(group: TGroup | null): void;
  selectedGroup?: null | TGroup;
};

export const GroupSelectForWellList = observer(function GroupSelectForWellList({
  open,
  groupSelectStore,
  selectedGroup,
  onOpenChange,
  onChangeGroup,
}: Props) {
  const { t } = useTranslation();

  const containerRef = useRef<HTMLUListElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const directories = useService('directories');

  const wells = directories.getObject('GOdashboard_Well');

  useOutsideClick(containerRef, () => onOpenChange(false), [buttonRef]);

  const renderPopoverContent = () => (
    <ul className={styles.groupsList} ref={containerRef}>
      {groupSelectStore.groupManager.groups.map((group) => {
        const selectedWell = groupSelectStore.getWellIdInGroup(group.id);
        const usesByAnotherWidgets = groupSelectStore.getUsesByAnotherWidgets(group.id);

        const wellName = wells?.find((item) => item.id === selectedWell)?.data.name ?? selectedWell;

        return (
          <li key={group.id} className={styles.groupItem}>
            <button
              type="button"
              className={clsx(styles.groupButton, selectedWell && styles.used)}
              onClick={() => {
                onChangeGroup(group);
                onOpenChange(false);
              }}
            >
              <figure className={styles.groupCircle} style={{ backgroundColor: group.color }} />
              <p className={styles.groupName}>
                {t('widgets:group', { groupId: group.id })}{' '}
                {!selectedWell && usesByAnotherWidgets ? `(${t('widgets:used')})` : ''}
                {selectedWell && `(${wellName})`}
              </p>
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className={styles.wrapper} ref={buttonRef}>
      {selectedGroup && (
        <Tooltip
          title={renderPopoverContent()}
          open={open}
          trigger={['click']}
          className={styles.popover}
          onOpenChange={onOpenChange}
          withArrow={true}
        >
          <button
            type="button"
            style={{ backgroundColor: selectedGroup.color }}
            className={clsx(styles.groupButtonForWellList)}
          >
            <AddRingFill className={clsx(styles.addIcon)} />
          </button>
        </Tooltip>
      )}
    </div>
  );
});
